import axios from "axios";
import { baseClient, baseUrl, getToken } from "./baseRequest";

export type SerialBatchFile = {
  /** Full object key within the bucket, e.g. "csv/serials12.csv". */
  name: string;
  sizeBytes: number;
  updatedAt: string;
};

export type GeneratedSerial = {
  serialNumber: string;
  friendlyIdentifier: string;
  clockId: string;
};

export type GenerateBatchResult = {
  batchName: string;
  requested: number;
  created: number;
  failed: { serialNumber: string; reason: string }[];
  csvUploadFailed: boolean;
  /** Only populated when the CSV upload failed, as a recovery fallback. */
  serials?: GeneratedSerial[];
};

/** Leaf filename of a bucket object key, e.g. "csv/serials12.csv" -> "serials12.csv". */
export const batchFileName = (key: string): string =>
  key.split("/").pop() ?? key;

export const saveBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export type SerialLookupResult = {
  query: string;
  clockId: string | null;
  serialNumber: string | null;
  friendlyIdentifier: string | null;
  email: string | null;
  authCreatedAt: string | null;
  registered: boolean;
  registrationEmail: string | null;
  registrationDate: string | null;
  version: number | null;
  orders: string[];
  giftCount: number;
};

export type GenerationStatus = {
  running: boolean;
  /** ISO time the running generation took the lock; null when not running. */
  startedAt: string | null;
};

/** A test-only serial, reserved so batch generation never reuses it. */
export type TestSerialEntry = {
  serialNumber: string;
  /** The friendly the clock shows for this serial. */
  friendlyIdentifier: string;
  addedAt: string;
  /** Firebase uid of the admin who added it. */
  addedBy: string | null;
  note: string | null;
};

export type CreatedClock = {
  uid: string;
  serialNumber: string;
  /** The friendly the clock shows, written to the machine doc idf. */
  friendlyIdentifier: string;
  testSerial: boolean;
};

export type SerialsClient = {
  listBatches(): Promise<SerialBatchFile[]>;
  generateBatch(count: number, prefix?: string): Promise<GenerateBatchResult>;
  generationStatus(): Promise<GenerationStatus>;
  lookup(query: string): Promise<SerialLookupResult>;
  downloadBatch(name: string): Promise<void>;
  listTestSerials(): Promise<TestSerialEntry[]>;
  addTestSerial(serial: string, note?: string): Promise<TestSerialEntry>;
  createClock(serial: string, testSerial: boolean): Promise<CreatedClock>;
};

const serialsClient: SerialsClient = {
  listBatches() {
    return baseClient.get<SerialBatchFile[]>({ url: "/admin/serials/batches" });
  },

  generateBatch(count: number, prefix?: string) {
    return baseClient.post<GenerateBatchResult>({
      url: "/admin/serials/batches",
      body: { count, prefix },
    });
  },

  generationStatus() {
    return baseClient.get<GenerationStatus>({
      url: "/admin/serials/generation/status",
    });
  },

  lookup(query: string) {
    return baseClient.get<SerialLookupResult>({
      url: "/admin/serials/lookup",
      params: { query },
    });
  },

  // The download needs the auth header, so it cannot be a plain link;
  // fetch it as a blob and trigger a browser download. `name` is the full
  // object key (e.g. "csv/serials12.csv").
  async downloadBatch(name: string) {
    const response = await axios.get(
      `${baseUrl}/admin/serials/batches/download`,
      {
        headers: { Authorization: `Bearer ${await getToken()}` },
        params: { file: name },
        responseType: "blob",
      },
    );
    saveBlob(new Blob([response.data]), batchFileName(name));
  },

  listTestSerials() {
    return baseClient.get<TestSerialEntry[]>({ url: "/admin/serials/test" });
  },

  addTestSerial(serial: string, note?: string) {
    return baseClient.post<TestSerialEntry>({
      url: "/admin/serials/test",
      body: { serial, note },
    });
  },

  // Creates the clock auth user, its role and machine doc. A test serial is
  // also recorded in the test serials file; any other serial must be in a
  // batch. The serial is validated before the clock exists.
  createClock(serial: string, testSerial: boolean) {
    return baseClient.post<CreatedClock>({
      url: "/admin/serials/clocks",
      body: { serial, testSerial },
    });
  },
};

export default serialsClient;
